import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Divider,
  Alert,
  LinearProgress,
  Tooltip,
  Badge,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  History as HistoryIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Build as ImplementIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreIcon,
  Description as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as GenericFileIcon,
  CloudUpload as CloudUploadIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Business as ProjectIcon,
  Notifications as NotificationIcon,
  Comment as CommentIcon,
  Assessment as ImpactIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Group as StakeholdersIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@mui/lab';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const DesignRevisions = () => {
  const { user } = useAuth();
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // create, edit, view, review, stakeholders
  const [selectedRevision, setSelectedRevision] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [tabValue, setTabValue] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [projects, setProjects] = useState([]);
  const [drawingsOptions, setDrawingsOptions] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    underReview: 0,
    approved: 0,
    implemented: 0,
    rejected: 0
  });

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    reason: '',
    priority: 'medium',
    impactLevel: 'medium',
    category: '',
    projectId: '',
    parentDrawingId: '',
    affectedDrawings: [],
    estimatedCost: '',
    estimatedDuration: '',
    requestedBy: '',
    targetDate: '',
    files: [],
    stakeholders: []
  });

  const priorities = ['low', 'medium', 'high', 'critical'];
  const impactLevels = ['low', 'medium', 'high', 'critical'];
  // Align categories with backend revisionType enum
  const categories = [
    'Design Change',
    'Code Compliance',
    'Client Request',
    'Field Condition',
    'Coordination',
    'Error Correction',
    'Value Engineering',
    'Other'
  ];
  const statuses = ['pending', 'under_review', 'approved', 'rejected', 'implemented', 'cancelled'];

  useEffect(() => {
    fetchRevisions();
    fetchStats();
  }, [searchTerm, statusFilter, priorityFilter]);

  // Fetch projects on mount
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await api.get('/projects');
        const data = res?.data;
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.projects)
            ? data.projects
            : Array.isArray(data?.data)
              ? data.data
              : [];
        setProjects(list);
      } catch (e) {
        console.error('Error fetching projects', e);
      }
    };
    fetchProjects();
  }, []);

  // Fetch drawings when project changes
  useEffect(() => {
    const fetchProjectDrawings = async () => {
      if (!formData.projectId) {
        setDrawingsOptions([]);
        return;
      }
      try {
        // Prefer unified drawings list endpoint with projectId filter
        const res = await api.get(`/drawings?projectId=${formData.projectId}`);
        const data = res?.data;
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.drawings)
            ? data.drawings
            : Array.isArray(data?.data)
              ? data.data
              : [];
        setDrawingsOptions(list);
      } catch (e) {
        try {
          const res = await api.get(`/drawings/project/${formData.projectId}`);
          const data = res?.data;
          const list = Array.isArray(data)
            ? data
            : Array.isArray(data?.drawings)
              ? data.drawings
              : Array.isArray(data?.data)
                ? data.data
                : [];
          setDrawingsOptions(list);
        } catch (err) {
          console.error('Error fetching project drawings', err);
          setDrawingsOptions([]);
        }
      }
    };
    fetchProjectDrawings();
  }, [formData.projectId]);

  const fetchRevisions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      
      const response = await api.get(`/design-revisions?${params.toString()}`);
      setRevisions(response.data);
    } catch (error) {
      console.error('Error fetching revisions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/design-revisions/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleOpenDialog = (mode, revision = null) => {
    setDialogMode(mode);
    setSelectedRevision(revision);
    if (revision) {
      setFormData({
        title: revision.title || '',
        description: revision.description || '',
        reason: revision.reason || '',
        priority: revision.priority || 'medium',
        impactLevel: revision.impactLevel || 'medium',
        category: revision.category || '',
        projectId: revision.projectId?._id || revision.project?._id || '',
        parentDrawingId: revision.parentDrawingId || '',
        affectedDrawings: revision.affectedDrawings || [],
        estimatedCost: revision.estimatedCost || '',
        estimatedDuration: revision.estimatedDuration || '',
        requestedBy: revision.requestedBy || '',
        targetDate: revision.targetDate ? revision.targetDate.split('T')[0] : '',
        files: revision.files || [],
        stakeholders: revision.stakeholders || []
      });
    } else {
      setFormData({
        title: '',
        description: '',
        reason: '',
        priority: 'medium',
        impactLevel: 'medium',
        category: '',
        projectId: '',
        parentDrawingId: '',
        affectedDrawings: [],
        estimatedCost: '',
        estimatedDuration: '',
        requestedBy: user?.name || '',
        targetDate: '',
        files: [],
        stakeholders: []
      });
    }
    setSelectedFiles([]);
    setReviewComment('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedRevision(null);
    setSelectedFiles([]);
    setUploadProgress(0);
    setReviewComment('');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
  };

  const handleSubmit = async () => {
    try {
      const submitData = new FormData();
      
      // Add form fields
      Object.keys(formData).forEach(key => {
        if (key === 'affectedDrawings' && Array.isArray(formData[key])) {
          formData[key].forEach(drawing => submitData.append('affectedDrawings[]', drawing));
        } else if (key === 'stakeholders' && Array.isArray(formData[key])) {
          formData[key].forEach(stakeholder => submitData.append('stakeholders[]', JSON.stringify(stakeholder)));
        } else if (key !== 'files') {
          submitData.append(key, formData[key]);
        }
      });

      // Explicitly add parentDrawingId for clarity
      if (formData.parentDrawingId) {
        submitData.append('parentDrawingId', formData.parentDrawingId);
      }
      
      // Add files
      selectedFiles.forEach(file => {
        submitData.append('files', file);
      });

      let response;
      if (dialogMode === 'create') {
        response = await api.post('/design-revisions', submitData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        });
      } else {
        response = await api.put(`/design-revisions/${selectedRevision._id}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        });
      }

      await fetchRevisions();
      await fetchStats();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving revision:', error);
    }
  };

  const handleReview = async (revisionId, action) => {
    try {
      await api.post(`/design-revisions/${revisionId}/review`, {
        action,
        comment: reviewComment
      });
      await fetchRevisions();
      await fetchStats();
      handleCloseDialog();
    } catch (error) {
      console.error('Error reviewing revision:', error);
    }
  };

  const handleApprove = async (revisionId) => {
    try {
      await api.post(`/design-revisions/${revisionId}/approve`);
      await fetchRevisions();
      await fetchStats();
    } catch (error) {
      console.error('Error approving revision:', error);
    }
  };

  const handleImplement = async (revisionId) => {
    try {
      await api.post(`/design-revisions/${revisionId}/implement`);
      await fetchRevisions();
      await fetchStats();
    } catch (error) {
      console.error('Error implementing revision:', error);
    }
  };

  const handleNotifyStakeholders = async (revisionId) => {
    try {
      await api.post(`/design-revisions/${revisionId}/notify`);
    } catch (error) {
      console.error('Error notifying stakeholders:', error);
    }
  };

  const handleDelete = async (revisionId) => {
    if (window.confirm('Are you sure you want to delete this revision?')) {
      try {
        await api.delete(`/design-revisions/${revisionId}`);
        await fetchRevisions();
        await fetchStats();
      } catch (error) {
        console.error('Error deleting revision:', error);
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'default',
      under_review: 'warning',
      approved: 'success',
      rejected: 'error',
      implemented: 'primary',
      cancelled: 'secondary'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'success',
      medium: 'warning',
      high: 'error',
      critical: 'error'
    };
    return colors[priority] || 'default';
  };

  const getImpactColor = (impact) => {
    const colors = {
      low: 'success',
      medium: 'warning',
      high: 'error',
      critical: 'error'
    };
    return colors[impact] || 'default';
  };

  const getFileIcon = (filename) => {
    const extension = filename.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf': return <PdfIcon />;
      case 'jpg': case 'jpeg': case 'png': case 'gif': return <ImageIcon />;
      case 'dwg': case 'dxf': return <FileIcon />;
      default: return <GenericFileIcon />;
    }
  };

  const filteredRevisions = revisions.filter(revision => {
    const matchesSearch = !searchTerm || 
      revision.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      revision.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      revision.category?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || revision.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || revision.priority === priorityFilter;
    
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const renderRevisionDialog = () => {
    return (
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' && 'Create New Design Revision'}
          {dialogMode === 'edit' && 'Edit Design Revision'}
          {dialogMode === 'view' && 'Design Revision Details'}
          {dialogMode === 'review' && 'Review Design Revision'}
          {dialogMode === 'stakeholders' && 'Manage Stakeholders'}
        </DialogTitle>
        <DialogContent>
          {dialogMode === 'review' ? (
            <Box>
              <Typography variant="h6" gutterBottom>
                Review: {selectedRevision?.title}
              </Typography>
              <Typography variant="body1" paragraph>
                {selectedRevision?.description}
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Priority:</Typography>
                  <Chip 
                    label={selectedRevision?.priority} 
                    color={getPriorityColor(selectedRevision?.priority)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Impact Level:</Typography>
                  <Chip 
                    label={selectedRevision?.impactLevel} 
                    color={getImpactColor(selectedRevision?.impactLevel)}
                    size="small"
                  />
                </Grid>
              </Grid>
              
              <TextField
                fullWidth
                label="Review Comment"
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                multiline
                rows={4}
                sx={{ mb: 3 }}
                required
              />
              
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => handleReview(selectedRevision._id, 'approve')}
                  startIcon={<ApproveIcon />}
                >
                  Approve
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => handleReview(selectedRevision._id, 'reject')}
                  startIcon={<RejectIcon />}
                >
                  Reject
                </Button>
              </Box>
            </Box>
          ) : dialogMode === 'stakeholders' ? (
            <Box>
              <Typography variant="h6" gutterBottom>
                Stakeholders for: {selectedRevision?.title}
              </Typography>
              <List>
                {selectedRevision?.stakeholders?.map((stakeholder, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <Avatar sx={{ width: 32, height: 32 }}>
                        {stakeholder.name?.charAt(0)}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={stakeholder.name}
                      secondary={
                        <Box>
                          <Typography variant="body2">{stakeholder.role}</Typography>
                          <Typography variant="caption">{stakeholder.email}</Typography>
                        </Box>
                      }
                    />
                    <Chip 
                      label={stakeholder.notificationStatus || 'pending'} 
                      size="small" 
                      color={stakeholder.notificationStatus === 'notified' ? 'success' : 'default'}
                    />
                  </ListItem>
                ))}
              </List>
              
              <Button
                variant="contained"
                startIcon={<NotificationIcon />}
                onClick={() => handleNotifyStakeholders(selectedRevision._id)}
                sx={{ mt: 2 }}
              >
                Notify All Stakeholders
              </Button>
            </Box>
          ) : (
            <Box>
              <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
                <Tab label="Basic Info" />
                <Tab label="Impact & Timeline" />
                <Tab label="Files" />
                <Tab label="Workflow" />
              </Tabs>
              
              {tabValue === 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Project</InputLabel>
                    <Select
                      value={formData.projectId}
                      onChange={(e) => handleInputChange('projectId', e.target.value)}
                      disabled={dialogMode === 'view'}
                    >
                      {(Array.isArray(projects) ? projects : []).map((p) => (
                        <MenuItem key={p._id || p.id} value={p._id || p.id}>
                          {p.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Parent Drawing</InputLabel>
                    <Select
                      value={formData.parentDrawingId}
                      onChange={(e) => handleInputChange('parentDrawingId', e.target.value)}
                      disabled={dialogMode === 'view' || !formData.projectId}
                    >
                      {drawingsOptions.map((d) => (
                        <MenuItem key={d._id} value={d._id}>
                          {d.drawingNumber} — {d.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Title"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    disabled={dialogMode === 'view'}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    disabled={dialogMode === 'view'}
                    multiline
                    rows={4}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Reason for Change"
                    value={formData.reason}
                    onChange={(e) => handleInputChange('reason', e.target.value)}
                    disabled={dialogMode === 'view'}
                    multiline
                    rows={3}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={formData.category}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                      disabled={dialogMode === 'view'}
                    >
                      {categories.map(category => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Requested By"
                    value={formData.requestedBy}
                    onChange={(e) => handleInputChange('requestedBy', e.target.value)}
                    disabled={dialogMode === 'view'}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Affected Drawings</InputLabel>
                    <Select
                      multiple
                      value={formData.affectedDrawings}
                      onChange={(e) => handleInputChange('affectedDrawings', e.target.value)}
                      disabled={dialogMode === 'view' || !formData.projectId}
                      renderValue={(selected) => {
                        const labels = drawingsOptions
                          .filter(d => selected.includes(d._id))
                          .map(d => `${d.drawingNumber}`);
                        return labels.join(', ');
                      }}
                    >
                      {drawingsOptions.map((d) => (
                        <MenuItem key={d._id} value={d._id}>
                          {d.drawingNumber} — {d.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                </Grid>
              )}
              
              {tabValue === 1 && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Priority</InputLabel>
                      <Select
                        value={formData.priority}
                        onChange={(e) => handleInputChange('priority', e.target.value)}
                        disabled={dialogMode === 'view'}
                      >
                        {priorities.map(priority => (
                          <MenuItem key={priority} value={priority}>
                            {priority.charAt(0).toUpperCase() + priority.slice(1)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Impact Level</InputLabel>
                      <Select
                        value={formData.impactLevel}
                        onChange={(e) => handleInputChange('impactLevel', e.target.value)}
                        disabled={dialogMode === 'view'}
                      >
                        {impactLevels.map(level => (
                          <MenuItem key={level} value={level}>
                            {level.charAt(0).toUpperCase() + level.slice(1)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Estimated Cost"
                      value={formData.estimatedCost}
                      onChange={(e) => handleInputChange('estimatedCost', e.target.value)}
                      disabled={dialogMode === 'view'}
                      type="number"
                      InputProps={{
                        startAdornment: '$'
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Estimated Duration (days)"
                      value={formData.estimatedDuration}
                      onChange={(e) => handleInputChange('estimatedDuration', e.target.value)}
                      disabled={dialogMode === 'view'}
                      type="number"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Target Date"
                      value={formData.targetDate}
                      onChange={(e) => handleInputChange('targetDate', e.target.value)}
                      disabled={dialogMode === 'view'}
                      type="date"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              )}
              
              {tabValue === 2 && (
                <Box>
                  {dialogMode !== 'view' && (
                    <Box sx={{ mb: 3 }}>
                      <input
                        accept=".pdf,.dwg,.dxf,.jpg,.jpeg,.png,.doc,.docx"
                        style={{ display: 'none' }}
                        id="file-upload"
                        multiple
                        type="file"
                        onChange={handleFileSelect}
                      />
                      <label htmlFor="file-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<CloudUploadIcon />}
                          fullWidth
                          sx={{ py: 2 }}
                        >
                          Select Files to Upload
                        </Button>
                      </label>
                      {selectedFiles.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Selected Files:
                          </Typography>
                          <List dense>
                            {selectedFiles.map((file, index) => (
                              <ListItem key={index}>
                                <ListItemIcon>
                                  {getFileIcon(file.name)}
                                </ListItemIcon>
                                <ListItemText
                                  primary={file.name}
                                  secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                      {uploadProgress > 0 && uploadProgress < 100 && (
                        <Box sx={{ mt: 2 }}>
                          <LinearProgress variant="determinate" value={uploadProgress} />
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Uploading... {uploadProgress}%
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                  
                  {formData.files.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Current Files:
                      </Typography>
                      <List>
                        {formData.files.map((file, index) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              {getFileIcon(file.filename)}
                            </ListItemIcon>
                            <ListItemText
                              primary={file.filename}
                              secondary={`${file.fileType} • ${(file.fileSize / 1024 / 1024).toFixed(2)} MB`}
                            />
                            <IconButton size="small">
                              <DownloadIcon />
                            </IconButton>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Box>
              )}
              
              {tabValue === 3 && selectedRevision && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Workflow Timeline
                  </Typography>
                  <Timeline>
                    {selectedRevision.workflowHistory?.map((step, index) => (
                      <TimelineItem key={index}>
                        <TimelineOppositeContent color="textSecondary">
                          {new Date(step.timestamp).toLocaleString()}
                        </TimelineOppositeContent>
                        <TimelineSeparator>
                          <TimelineDot color={step.action === 'approved' ? 'success' : step.action === 'rejected' ? 'error' : 'primary'}>
                            {step.action === 'approved' && <ApproveIcon />}
                            {step.action === 'rejected' && <RejectIcon />}
                            {step.action === 'implemented' && <ImplementIcon />}
                            {!['approved', 'rejected', 'implemented'].includes(step.action) && <AssignmentIcon />}
                          </TimelineDot>
                          {index < selectedRevision.workflowHistory.length - 1 && <TimelineConnector />}
                        </TimelineSeparator>
                        <TimelineContent>
                          <Typography variant="h6" component="span">
                            {step.action.charAt(0).toUpperCase() + step.action.slice(1)}
                          </Typography>
                          <Typography>{step.comment}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            By {step.user?.name}
                          </Typography>
                        </TimelineContent>
                      </TimelineItem>
                    ))}
                  </Timeline>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          {dialogMode !== 'view' && dialogMode !== 'review' && dialogMode !== 'stakeholders' && (
            <Button onClick={handleSubmit} variant="contained">
              {dialogMode === 'create' ? 'Create' : 'Update'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading design revisions...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Design Revisions
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Manage design changes and revision workflows
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog('create')}
        >
          Create Revision
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total
                  </Typography>
                  <Typography variant="h5">
                    {stats.total}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <AssignmentIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Pending
                  </Typography>
                  <Typography variant="h5">
                    {stats.pending}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'grey.500' }}>
                  <ScheduleIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Under Review
                  </Typography>
                  <Typography variant="h5">
                    {stats.underReview}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <HistoryIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Approved
                  </Typography>
                  <Typography variant="h5">
                    {stats.approved}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <ApproveIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Implemented
                  </Typography>
                  <Typography variant="h5">
                    {stats.implemented}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <ImplementIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Rejected
                  </Typography>
                  <Typography variant="h5">
                    {stats.rejected}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'error.main' }}>
                  <RejectIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search revisions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  {statuses.map(status => (
                    <MenuItem key={status} value={status}>
                      {status.replace('_', ' ').charAt(0).toUpperCase() + status.replace('_', ' ').slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                >
                  <MenuItem value="all">All Priorities</MenuItem>
                  {priorities.map(priority => (
                    <MenuItem key={priority} value={priority}>
                      {priority.charAt(0).toUpperCase() + priority.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setPriorityFilter('all');
                }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Revisions Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Impact</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Requested By</TableCell>
                  <TableCell>Target Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRevisions.map((revision) => (
                  <TableRow key={revision._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {revision.title}
                      </Typography>
                      {revision.description && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          {revision.description.substring(0, 60)}...
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={revision.category} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={revision.priority} 
                        color={getPriorityColor(revision.priority)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={revision.impactLevel} 
                        color={getImpactColor(revision.impactLevel)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={revision.status?.replace('_', ' ')} 
                        color={getStatusColor(revision.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {revision.requestedBy}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {revision.targetDate ? new Date(revision.targetDate).toLocaleDateString() : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenDialog('view', revision)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenDialog('edit', revision)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Stakeholders">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenDialog('stakeholders', revision)}
                          >
                            <StakeholdersIcon />
                          </IconButton>
                        </Tooltip>
                        {revision.status === 'under_review' && (
                          <Tooltip title="Review">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleOpenDialog('review', revision)}
                            >
                              <CommentIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {revision.status === 'approved' && (
                          <Tooltip title="Implement">
                            <IconButton 
                              size="small" 
                              color="success"
                              onClick={() => handleImplement(revision._id)}
                            >
                              <ImplementIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDelete(revision._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {filteredRevisions.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No design revisions found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {searchTerm || statusFilter !== 'all' || priorityFilter !== 'all'
                  ? 'Try adjusting your search criteria'
                  : 'Get started by creating your first design revision'
                }
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {renderRevisionDialog()}
    </Box>
  );
};

export default DesignRevisions;